import type { ReactNode } from "react";

import { classes } from "./classes.js";
import {
  FOND_SURFACE,
  RAYON_MODALE,
  SURFACE_BORDER,
  TEXTE_PRINCIPAL,
  TEXTE_SECONDAIRE,
  VOILE_MODALE,
} from "./tokens.js";

/**
 * `P0-UIK-001`: a centred dialog with a title, a body and its actions.
 *
 * Renders NOTHING when closed rather than hiding with a class. A hidden dialog
 * still exists for a screen reader and still holds focus targets, and the
 * cheapest way to be sure a closed modal is closed is for it not to be there.
 *
 * ## `round F6b`: it lays a VEIL over the page, because that is what it is drawn as
 *
 * The founder's reading of pair 23 on 9 September: the removal dialog must dim
 * the page behind it as the drawing does. It did not , this component rendered
 * a centred card over an undimmed page, so a dialog interrupting a screen full
 * of a person's own cars looked like part of that screen.
 *
 * The drawing gives every modal one structure (`hakili-dealer.jsx`,
 * `ModaleRetrait:3134`, and `PanneauAjout`, `PanneauInvitation`, the
 * feature-request modal): a fixed full-screen veil that CLOSES when clicked,
 * the panel over it, and an `X` in the panel's corner.
 *
 * **The veil is a SIBLING of the panel and not its parent**, which is where
 * this departs from the drawing's letter to keep its meaning. The mockups make
 * the veil the container and stop the click propagating out of the panel; that
 * works, and it means every future click handler inside the panel is one
 * forgotten `stopPropagation` away from closing the dialog under the person
 * using it. As siblings, a click inside the panel never reaches the veil at
 * all. `SidePanel` has been built this way since `F2.3` and this now matches
 * it, so the two dialogs of this design system are one shape.
 *
 * **The veil is not decoration.** It says the page underneath is waiting rather
 * than gone, and clicking it is the second gesture a person tries after the
 * cross , `SidePanel` says the same thing in its own words and has said it
 * since `F2.3`.
 */
export interface ModalProps {
  readonly open: boolean;
  readonly title: ReactNode;
  readonly children: ReactNode;
  readonly actions?: ReactNode;
  readonly className?: string;
  /**
   * The cross, and the veil that closes on a click.
   *
   * Optional, and its absence is meaningful, exactly as it is on `SidePanel`: a
   * dialog with no way out drawn on it is one a person must guess at, so a
   * caller that omits this is saying the dialog closes some other way and is
   * taking responsibility for saying how. The veil is drawn either way ,
   * dimming the page is true whether or not clicking it is a way out.
   */
  readonly onClose?: () => void;
  /** What the cross is called, for a reader who cannot see it. */
  readonly closeLabel?: string;
  /**
   * `false` draws no cross while `onClose` still serves the veil: a dialog whose drawing offers « Annuler » and no ✕ (D2's Équipe
   * modals, `EQUIPE-GESTURES` R4). Default `true`: every other dialog keeps its cross.
   */
  readonly cross?: boolean;
  /** `F-0b`: `wide` is the bank's review window (a piece beside its checks); `md` is every other dialog, unchanged; `lg` is the bank's gesture dialogs at D2's 640 px (`EQUIPE-GESTURES` R5). */
  readonly size?: "md" | "wide" | "lg";
}

export function Modal({
  open,
  title,
  children,
  actions,
  className,
  onClose,
  closeLabel,
  cross = true,
  size = "md",
}: ModalProps): React.JSX.Element | null {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-6">
      {/* The page, dimmed and still there. `aria-hidden` because it is the
          page's dimming and not a control: a reader who cannot see it is
          offered the cross, which is a button and says what it does. */}
      <div
        className={classes("absolute inset-0", VOILE_MODALE)}
        onClick={onClose}
        aria-hidden="true"
        data-testid="modale-fond"
      />
      {/* **`RAYON_MODALE`, and round 203's design-system survey is why.** This
          wore `rounded-2xl`, the PANEL radius, while every modal in all four
          mockups wears `rounded-3xl` , the one place the design uses it. It
          was the single drift the survey found across every primitive, and the
          architect ruled it cured here rather than in a second round touching
          the same file. */}
      <div
        role="dialog"
        data-kit="modal"
        data-size={size}
        aria-modal="true"
        className={classes(
          size === "wide"
            ? "relative w-full max-w-3xl border p-6"
            : size === "lg"
              ? "relative w-full max-w-[640px] border p-6"
              : "relative w-full max-w-md border p-6",
          RAYON_MODALE,
          SURFACE_BORDER,
          FOND_SURFACE,
          className,
        )}
      >
        <div className="flex items-start justify-between gap-3">
          <h2 className={classes("text-lg font-semibold", TEXTE_PRINCIPAL)}>{title}</h2>
          {onClose === undefined || !cross ? null : (
            <button
              type="button"
              onClick={onClose}
              aria-label={closeLabel}
              className="-mt-1 rounded-lg p-1 text-gray-400 hover:text-gray-600"
              data-testid="modale-fermer"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                className="h-4 w-4"
                aria-hidden="true"
              >
                <path d="M6 6l12 12M18 6L6 18" />
              </svg>
            </button>
          )}
        </div>
        <div className={classes("mt-4 text-sm", TEXTE_SECONDAIRE)}>{children}</div>
        {actions === undefined ? null : (
          <div className="mt-6 flex justify-end gap-3" data-modal="actions">
            {actions}
          </div>
        )}
      </div>
    </div>
  );
}
